import SubscriptionProfilesToken from '@apps/truevail/admin/tokens/subscription_profiles'
import SchedulingToken from '@apps/truevail/admin/tokens/scheduling'
import TimeframeToken from '@apps/truevail/admin/tokens/timeframe'
import Details from '@admin/components/details'
import Button from '@admin/components/button'
import Panel from '@admin/components/panel'
import Transition from '../transition'
import PropTypes from 'prop-types'
import moment from 'moment'
import Edit from '../edit'
import React from 'react'

const types = {
  social: 't(Social Media)',
  email: 't(Email)',
  legacy_website: 't(Legacy Website)',
  website: 't(Website)'
}

const Overview = ({ advisor, subscription }) => {

  const advisor_button = {
    label: advisor.team.title,
    className: 'link',
    route: `/truevail/agency/advisors/${advisor.id}`
  }

  const sender = subscription.sender ? {
    label: subscription.sender.rfc822,
    className: 'link',
    link: `/${advisor.team.subdomain}/team/senders/${subscription.sender.id}`
  } : null

  const list = subscription.list ? {
    label: subscription.list.title,
    className: 'link',
    link: `/${advisor.team.subdomain}/crm/lists/${subscription.list.id}`
  } : null

  const email_template = subscription.email_template ? {
    label: subscription.email_template.title,
    className: 'link',
    link: `/${advisor.team.subdomain}/team/templates/${subscription.email_template.id}`
  } : null

  const url = subscription.url ? {
    label: subscription.url,
    className: 'link',
    link: subscription.url
  } : null

  const login_url = subscription.login_url ? {
    label: subscription.login_url,
    className: 'link',
    link: subscription.login_url
  } : null

  const unschedule = {
    label: 't(Unschedule Transition)',
    className: 'link',
    confirm: 't(Are you sure you want to unschedule this transition?)',
    request: {
      endpoint: `/api/admin/truevail/admin/advisors/${advisor.id}/subscriptions/${subscription.id}/transition`,
      method: 'PATCH',
      body: {
        action: 'unschedule'
      }
    }
  }

  const details = {
    ...subscription.deleted_at ? {
      alert: { color: 'red', message: 't(This subscription was deleted)' }
    } : subscription.status === 'active' ? {
      alert: { color: 'green', message: 't(This subscription is active)' }
    } : subscription.status === 'inactive' ? {
      alert: { color: 'grey', message: 't(This subscription is inactive)' }
    } : subscription.status === 'pending' ? {
      alert: { color: 'teal', message: 't(This subscription is pending)' }
    } : subscription.status === 'pending' ? {
      alert: { color: 'red', message: 't(This subscription is inactive)' }
    } : subscription.status === 'paused' ? {
      alert: { color: 'yellow', message: 't(This subscription is paused)' }
    } : subscription.status === 'activating' ? {
      alert: { color: 'teal', message: (
        <>This subscription will be activated on { moment(subscription.transition_at).format('MM/DD/YY [@] h:mm A') }. <Button { ...unschedule } /></>
      ) }
    } : subscription.status === 'pausing' ? {
      alert: { color: 'teal', message: (
        <>This subscription will be paused on { moment(subscription.transition_at).format('MM/DD/YY [@] h:mm A') }. <Button { ...unschedule } /></>
      ) }
    } : subscription.status === 'resuming' ? {
      alert: { color: 'teal', message: (
        <>This subscription will be resumed on { moment(subscription.transition_at).format('MM/DD/YY [@] h:mm A') }. <Button { ...unschedule } /></>
      ) }
    } : subscription.status === 'reactivating' ? {
      alert: { color: 'teal', message: (
        <>This subscription will be reactivated on { moment(subscription.transition_at).format('MM/DD/YY [@] h:mm A') }. <Button { ...unschedule } /></>
      ) }
    } : subscription.status === 'canceling' ? {
      alert: { color: 'teal', message: (
        <>This subscription will be canceled on { moment(subscription.transition_at).format('MM/DD/YY [@] h:mm A') }. <Button { ...unschedule } /></>
      ) }
    } : subscription.status === 'canceled' ? {
      alert: { color: 'red', message: 't(This subscription was canceled)' }
    } : {},
    audits: `truevail_subscriptions/${subscription.id}`,
    comments: `truevail_subscriptions/${subscription.id}`,
    sections: [
      {
        title: 't(Subscription Details)',
        items: [
          { label: 't(Advisor)', content: <Button { ...advisor_button } /> },
          { label: 't(Type)', content: types[subscription.type] },
          ...subscription.type === 'email' ? [
            { label: 't(Sender)', content: subscription.sender ? <Button { ...sender } /> : null },
            { label: 't(List)', content: subscription.list ? <Button { ...list } /> : null },
            { label: 't(Email Template)', content: subscription.email_template ? <Button { ...email_template } /> : null },
            { label: 't(Scheduling)', content: <SchedulingToken value={ subscription.scheduling } /> },
            { label: 't(Reply To URL)', content: subscription.config?.reply_to_url || null }
          ] : subscription.type === 'social' ? [
            { label: 't(Profiles)', content: subscription.subscription_profiles.length > 0 ? <SubscriptionProfilesToken subscription_profiles={ subscription.subscription_profiles } /> : null },
            { label: 't(Scheduling)', content: subscription.scheduling ? <SchedulingToken value={ subscription.scheduling } /> : null },
            { label: 't(Scheduling Timeframe)', content: subscription.scheduling_timeframe ? <TimeframeToken value={ subscription.scheduling_timeframe } /> : null },
            { label: 't(Allow Randomization)', content: subscription.allow_randomization ? 't(Yes)' : 't(No)' },
            { label: 't(Before Caption)', content: subscription.config?.before_caption ? (
              <div className="truevail-preview-message" dangerouslySetInnerHTML={{ __html: subscription.config.before_caption.split('\n').join('<br />') }} />
            ) : null },
            { label: 't(After Caption)', content: subscription.config?.after_caption ? (
              <div className="truevail-preview-message" dangerouslySetInnerHTML={{ __html: subscription.config.after_caption.split('\n').join('<br />') }} />
            ) : null },
            { label: 't(Share Reels to Instagram Feed)', content: subscription.config?.share_reels_to_instagram_feed !== false ? 't(Yes)' : 't(No)' }
          ] : subscription.type === 'legacy_website' ? [
            { label: 't(Url)', content: subscription.url ? <Button { ...url } /> : null },
            { label: 't(Login Url)', content: subscription.login_url ? <Button { ...login_url } /> : null }
          ] : subscription.type === 'website' ? [
            { label: 't(Post Author)', content: subscription.post_author ? subscription.post_author.full_name : null },
            { label: 't(Post Template)', content: subscription.post_template ? subscription.post_template.title : null },
            { label: 't(Scheduling)', content: subscription.scheduling ? <SchedulingToken value={ subscription.scheduling } /> : null }
          ] : [],
          ...subscription.type !== 'legacy_website' ? [
            { label: 't(Channels)', content: subscription.channels.length > 0 ? subscription.channels.map(((channel, index) => (
              <div className="crm-tag-token" key={`channel_${index}`}>{ channel.title }</div>
            ))) : null }
          ] : [],
          ...['email','social'].includes(subscription.type) ? [
            { label: 't(Target Language)', content: subscription.target_language }
          ] : []
        ]
      }
    ]
  }

  return <Details { ...details } />

}

Overview.propTypes = {
  advisor: PropTypes.object,
  subscription: PropTypes.object
}

const mapPropsToPanel = (props, context) => ({
  title: 't(Overview)',
  panel: <Overview advisor={ props.advisor } subscription={ props.subscription } />,
  tasks: {
    items: [
      { 
        label: 't(Edit Subscription)', 
        svg: 'square_pen',
        modal: <Edit advisor={ props.advisor } subscription={ props.subscription } /> 
      },
      {
        label: 't(Activate Subscription)',
        svg: 'check',
        show: props.subscription.status === 'pending',
        modal: <Transition advisor={ props.advisor } subscription={ props.subscription } action="activate" />
      },
      {
        label: 't(Pause Subscription)',
        svg: 'circle_pause',
        show: props.subscription.status === 'active',
        modal: <Transition advisor={ props.advisor } subscription={ props.subscription } action="pause" />
      },
      {
        label: 't(Cancel Subscription)',
        svg: 'ban',
        show: props.subscription.status === 'active',
        modal: <Transition advisor={ props.advisor } subscription={ props.subscription } action="cancel" />
      },
      {
        label: 't(Resume Subscription)',
        svg: 'circle_pause',
        show: props.subscription.status === 'paused',
        modal: <Transition advisor={ props.advisor } subscription={ props.subscription } action="resume" />
      },
      {
        label: 't(Reactivate Subscription)',
        svg: 'check',
        show: ['inactive','canceled'].includes(props.subscription.status),
        modal: <Transition advisor={ props.advisor } subscription={ props.subscription } action="reactivate" />
      }
    ]
  }
})

export default Panel(null, mapPropsToPanel)